import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, type Icon } from '@tabler/icons-react';
import { getDropdownPosition } from './dropdownPosition';
import { useDropdownDismiss } from './useDropdownDismiss';
import './dropdown.css';

/**
 * Type A — action picker. A right-aligned, content-width menu of selectable
 * actions. Each option renders an optional colour dot (status) or icon (log
 * call), a label, and a check on the active option. Destructive options render
 * after a divider in a muted style.
 *
 * Portals to <body>, positions against `anchorRef`, dismisses on outside-click
 * + Escape.
 */
export interface ActionPickerOption {
  value: string;
  label: string;
  /** Colour dot (status variant). */
  colour?: string;
  /** Leading icon (log-call variant) — a tabler icon component. */
  Icon?: Icon;
  /** Render after a divider, in the muted danger style. */
  destructive?: boolean;
}

interface ActionPickerProps {
  options: ActionPickerOption[];
  currentValue: string;
  onSelect: (value: string) => void;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
  /** Force opening above the trigger (e.g. triggers near the viewport bottom). */
  preferAbove?: boolean;
}

export const ActionPicker: React.FC<ActionPickerProps> = ({
  options,
  currentValue,
  onSelect,
  onClose,
  anchorRef,
  preferAbove,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  useDropdownDismiss(dropdownRef, anchorRef, onClose);

  if (!anchorRef.current) return null;

  const position = getDropdownPosition(anchorRef.current, { preferAbove, align: 'right' });
  const normal = options.filter((o) => !o.destructive);
  const destructive = options.filter((o) => o.destructive);

  const renderItem = (option: ActionPickerOption) => {
    const Icon = option.Icon;
    const active = option.value === currentValue;
    return (
      <div
        key={option.value}
        className={`sm4rt-dd__item ${active ? 'sm4rt-dd__item--active' : ''} ${
          option.destructive && !active ? 'sm4rt-dd__item--danger' : ''
        }`}
        onClick={() => {
          onSelect(option.value);
          onClose();
        }}
      >
        {option.colour && (
          <span className="sm4rt-dd__dot" style={{ background: option.colour }} />
        )}
        {Icon && <Icon className="sm4rt-dd__icon" size={13} stroke={1.5} />}
        <span className="sm4rt-dd__label">{option.label}</span>
        {active && <IconCheck className="sm4rt-dd__check" size={12} />}
      </div>
    );
  };

  return createPortal(
    <div className="sm4rt-dd sm4rt-dd--menu" ref={dropdownRef} style={position}>
      <div className="sm4rt-dd__list">
        {normal.map(renderItem)}
        {destructive.length > 0 && <div className="sm4rt-dd__divider" />}
        {destructive.map(renderItem)}
      </div>
    </div>,
    document.body,
  );
};
